import React, { useMemo } from 'react';
import {
     Box,
     Typography,
     Select,
     MenuItem,
     FormControl,
     IconButton,
     Tooltip,
} from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { MilestoneSelectorProps } from '../types';
import { styles } from '../../MilestoneModal.styles';
import { getMilestoneSelectorStyles, getAddMilestoneButtonStyles } from './MilestoneSelector.styles';
import { useMilestoneSelector } from './useMilestoneSelector';
import { DIVISION_SETTINGS_MAX_NUMBER_OF_ADDITIONAL_MILESTONES } from '../../../../../../../../utils/constants';

export const MilestoneSelector: React.FC<MilestoneSelectorProps> = ({
     selectedMilestone,
     currentMilestones,
     onMilestoneSelect,
     onAddMilestone,
     isAddingMilestone,
}) => {
     const { sortedMilestones } = useMilestoneSelector({
          currentMilestones,
          selectedMilestone,
          onMilestoneSelect,
     });

     const disableAddMilestoneButton = useMemo(() => {
          const namesWithPredefined = new Set<string>();
          const namesWithCustom = new Set<string>();
          let customOnlyCount = 0;

          for (const milestoneRecord of currentMilestones) {
               if (milestoneRecord.isDefault !== null) {
                    namesWithPredefined.add(milestoneRecord.mileStoneName);
               } else {
                    namesWithCustom.add(milestoneRecord.mileStoneName);
               }
          }

          namesWithCustom.forEach((name) => {
               if (!namesWithPredefined.has(name)) customOnlyCount++;
          });

          return customOnlyCount >= DIVISION_SETTINGS_MAX_NUMBER_OF_ADDITIONAL_MILESTONES
     }, [currentMilestones]);

     /* Custom or Predefined */
     const milestoneType = useMemo(() => {
          return new Set(currentMilestones.filter((milestone) => milestone.isDefault !== null).map((milestone) => milestone.mileStoneName));
     }, [currentMilestones])

     if (isAddingMilestone) {
          return null;
     }

     return (
          <Box>
               <Typography variant="body1" sx={styles.sectionTitle}>
                    Milestones
               </Typography>
               <Box sx={getMilestoneSelectorStyles()}>
                    <FormControl sx={{ flex: 1 }}>
                         <Select
                              value={selectedMilestone}
                              onChange={(e) => onMilestoneSelect(e.target.value)}
                              label="Milestone"
                              variant="standard"
                              renderValue={(value) => value}
                              sx={styles.select}
                         >
                              {/* Show sorted milestones */}
                              {sortedMilestones.map((milestone: any) => {
                                   const type = milestoneType.has(milestone.mileStoneName) ? "Predefined" : <span style={{ color: "red" }}>Custom</span>;
                                   return (
                                        <MenuItem key={milestone.mileStoneName} value={milestone.mileStoneName}>
                                             {milestone.mileStoneName}
                                             <Typography
                                                  component="span"
                                                  sx={{
                                                       ml: 1,
                                                       fontSize: '0.75rem',
                                                       color: '#666',
                                                       fontStyle: 'italic'
                                                  }}
                                             >
                                                  ({type})
                                             </Typography>
                                        </MenuItem>
                                   );
                              })}
                         </Select>
                    </FormControl>
                    <Tooltip title={disableAddMilestoneButton ? "Maximum number of milestones reached" : "Add new milestone"} arrow placement="top">
                         <span>
                              <IconButton
                                   onClick={onAddMilestone}
                                   sx={getAddMilestoneButtonStyles()}
                                   disabled={disableAddMilestoneButton}
                                   data-testid="add-milestone-btn"
                              >
                                   <PlaylistAddIcon color={disableAddMilestoneButton ? "disabled" : "primary"} />
                              </IconButton>
                         </span>
                    </Tooltip>
               </Box>
          </Box>
     );
}; 